import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverAnchor } from '../components/ui/popover';
import { useCan } from '../contexts/AuthContext';
import { toast, Toaster } from 'sonner';
import {
    ArrowLeft, AlertOctagon, Plus, Trash2, FileText, Upload,
    X, Pencil, Save, Loader2, Image as ImageIcon, Paperclip, Search,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_LABELS = {
    a_traiter: { label: 'À traiter', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
    en_cours: { label: 'En cours', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    resolu: { label: 'Résolu', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    rejete: { label: 'Rejeté', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const ACCEPT_FILES = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*';
const MAX_BYTES = 10 * 1024 * 1024;

function isImage(name) {
    return /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(name || '');
}

function formatDate(iso) {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return iso;
    }
}

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}

async function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export default function Litiges() {
    const navigate = useNavigate();
    const { canWrite, canDelete } = useCan('clients');
    const [items, setItems] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState({});
    const [search, setSearch] = useState('');
    const [statutFilter, setStatutFilter] = useState('all');
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [litRes, cliRes] = await Promise.all([
                axios.get(`${API_URL}/api/litiges`, { withCredentials: true }),
                axios.get(`${API_URL}/api/clients`, { withCredentials: true }),
            ]);
            setItems(litRes.data || []);
            setAllClients(cliRes.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur de chargement');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const startNew = () => {
        setEditingId('new');
        setDraft({
            client_id: null,
            client_nom: '',
            date: todayISO(),
            description: '',
            cout: '',
            statut: 'a_traiter',
            files: [],
            new_files: [], // {file_data, file_name}
        });
    };

    const startEdit = (it) => {
        setEditingId(it.id);
        setDraft({
            client_id: it.client_id,
            client_nom: it.client_nom,
            date: it.date,
            description: it.description,
            cout: it.cout,
            statut: it.statut,
            files: it.files || [], // already-uploaded files (kept by default)
            new_files: [],
        });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setDraft({});
    };

    const handleFiles = async (filesList) => {
        if (!filesList || !filesList.length) return;
        const next = [];
        for (const file of Array.from(filesList)) {
            if (file.size > MAX_BYTES) {
                toast.error(`${file.name} dépasse 10 Mo`);
                continue;
            }
            try {
                const b64 = await fileToBase64(file);
                next.push({ file_data: b64, file_name: file.name });
            } catch {
                toast.error(`Lecture impossible: ${file.name}`);
            }
        }
        if (next.length) {
            setDraft((d) => ({ ...d, new_files: [...(d.new_files || []), ...next] }));
            toast.success(`${next.length} fichier(s) prêt(s) à être enregistré(s)`);
        }
        // Reset input so the same file can be re-selected if needed
        if (fileRef.current) fileRef.current.value = '';
    };

    const removeKeptFile = (idx) => {
        setDraft((d) => ({ ...d, files: d.files.filter((_, i) => i !== idx) }));
    };
    const removeNewFile = (idx) => {
        setDraft((d) => ({ ...d, new_files: d.new_files.filter((_, i) => i !== idx) }));
    };

    const save = async () => {
        if (!draft.client_nom?.trim()) {
            toast.error('Le nom du client est requis');
            return;
        }
        setSaving(true);
        try {
            // The backend expects a single `files` array containing both
            // already-uploaded entries (with `url`) and new ones
            // (with `file_data`). Whatever we send replaces the previous list.
            const filesPayload = [
                ...(draft.files || []).map((f) => ({ url: f.url, name: f.name })),
                ...(draft.new_files || []),
            ];
            const payload = {
                client_id: draft.client_id || null,
                client_nom: draft.client_nom,
                date: draft.date,
                description: draft.description,
                cout: Number(draft.cout || 0),
                statut: draft.statut,
                files: filesPayload,
            };
            if (editingId === 'new') {
                await axios.post(`${API_URL}/api/litiges`, payload, { withCredentials: true });
                toast.success('Litige enregistré');
            } else {
                await axios.put(`${API_URL}/api/litiges/${editingId}`, payload, { withCredentials: true });
                toast.success('Litige mis à jour');
            }
            cancelEdit();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (it) => {
        if (!window.confirm(`Supprimer le litige de ${it.client_nom} ?`)) return;
        try {
            await axios.delete(`${API_URL}/api/litiges/${it.id}`, { withCredentials: true });
            toast.success('Litige supprimé');
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur');
        }
    };

    const updateStatut = async (it, statut) => {
        try {
            await axios.put(`${API_URL}/api/litiges/${it.id}`, { statut }, { withCredentials: true });
            toast.success('Statut mis à jour');
            await load();
        } catch {
            toast.error('Erreur');
        }
    };

    const total = items.length;
    const aTraiter = items.filter((x) => x.statut === 'a_traiter').length;

    const filteredItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        return items.filter((it) => {
            if (statutFilter !== 'all' && it.statut !== statutFilter) return false;
            if (q) {
                const hay = [it.client_nom, it.description, String(it.cout || '')]
                    .filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [items, search, statutFilter]);

    return (
        <Layout>
            <Toaster position="top-right" />
            <div className="space-y-6 animate-fadeIn" data-testid="litiges-page">
                {/* Header */}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} className="h-8 w-8 p-0" data-testid="litiges-back">
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div className="h-10 w-10 rounded-full bg-red-50 flex items-center justify-center">
                            <AlertOctagon className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Litiges
                            </h1>
                            <p className="text-gray-500 mt-0.5 text-sm">Suivi des réclamations clients</p>
                        </div>
                    </div>
                    <Button
                        onClick={startNew}
                        disabled={editingId !== null || !canWrite}
                        title={!canWrite ? 'Accès refusé : permission Écriture sur Clients requise' : ''}
                        className="bg-[#1B365D] hover:bg-[#2A4A7A] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="litige-add-btn"
                    >
                        <Plus className="w-4 h-4 mr-1" /> Nouveau litige
                    </Button>
                </div>

                <Card className="shadow-sm">
                    <CardContent className="p-3 flex flex-col md:flex-row gap-3">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder="Rechercher (client, description, coût)…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 h-10 border-0 focus-visible:ring-0"
                                data-testid="litiges-search-input"
                            />
                        </div>
                        <Select value={statutFilter} onValueChange={setStatutFilter}>
                            <SelectTrigger className="w-full md:w-52 h-10" data-testid="litiges-statut-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {Object.entries(STATUT_LABELS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </CardContent>
                </Card>

                <Card className="shadow-sm">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <AlertOctagon className="w-5 h-5 text-amber-600" />
                            <h2 className="text-lg font-semibold text-[#1B365D]">
                                Problèmes ({filteredItems.length}{filteredItems.length !== total ? ` / ${total}` : ''}{aTraiter > 0 ? ` · ${aTraiter} à traiter` : ''})
                            </h2>
                        </div>

                        {loading ? (
                            <div className="py-10 flex items-center justify-center">
                                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-gray-500 border-b text-xs uppercase tracking-wide">
                                            <th className="py-3 px-3 w-56">Client</th>
                                            <th className="py-3 px-3 w-32">Date</th>
                                            <th className="py-3 px-3">Description</th>
                                            <th className="py-3 px-3 w-44">Pièces jointes</th>
                                            <th className="py-3 px-3 w-32">Coût</th>
                                            <th className="py-3 px-3 w-36">Statut</th>
                                            <th className="py-3 px-3 w-24 text-right">Actes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {editingId === 'new' && (
                                            <EditableRow
                                                draft={draft} setDraft={setDraft}
                                                onSave={save} onCancel={cancelEdit}
                                                onFiles={handleFiles}
                                                onRemoveKept={removeKeptFile} onRemoveNew={removeNewFile}
                                                fileRef={fileRef} saving={saving}
                                                allClients={allClients}
                                            />
                                        )}

                                        {items.length === 0 && editingId !== 'new' ? (
                                            <tr>
                                                <td colSpan={7} className="py-10 text-center text-gray-400">
                                                    Aucun litige. Cliquez sur « Nouveau litige » pour en ajouter un.
                                                </td>
                                            </tr>
                                        ) : filteredItems.length === 0 && editingId !== 'new' ? (
                                            <tr>
                                                <td colSpan={7} className="py-10 text-center text-gray-400">
                                                    Aucun litige ne correspond à vos filtres.
                                                </td>
                                            </tr>
                                        ) : filteredItems.map((it) => (
                                            editingId === it.id ? (
                                                <EditableRow
                                                    key={it.id}
                                                    draft={draft} setDraft={setDraft}
                                                    onSave={save} onCancel={cancelEdit}
                                                    onFiles={handleFiles}
                                                    onRemoveKept={removeKeptFile} onRemoveNew={removeNewFile}
                                                    fileRef={fileRef} saving={saving}
                                                    allClients={allClients}
                                                />
                                            ) : (
                                                <tr key={it.id} className="border-b hover:bg-gray-50/40 align-top">
                                                    <td className="py-3 px-3 font-semibold text-[#1B365D]">{it.client_nom}</td>
                                                    <td className="py-3 px-3 text-gray-600">{formatDate(it.date)}</td>
                                                    <td className="py-3 px-3 text-gray-700 whitespace-pre-wrap max-w-xs">
                                                        {it.description || <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="py-3 px-3">
                                                        <FilesList files={it.files} />
                                                    </td>
                                                    <td className="py-3 px-3 text-gray-700">
                                                        {it.cout ? `${it.cout.toLocaleString('fr-FR')} €` : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className="py-3 px-3">
                                                        <Select value={it.statut} onValueChange={(v) => updateStatut(it, v)} disabled={!canWrite}>
                                                            <SelectTrigger
                                                                className="h-8 w-32 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                data-testid={`litige-statut-${it.id}`}
                                                                title={!canWrite ? 'Accès refusé : permission Écriture requise' : ''}
                                                            >
                                                                <SelectValue>
                                                                    <Badge className={`text-xs border ${STATUT_LABELS[it.statut]?.cls || ''}`}>
                                                                        {STATUT_LABELS[it.statut]?.label || it.statut}
                                                                    </Badge>
                                                                </SelectValue>
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {Object.entries(STATUT_LABELS).map(([k, v]) => (
                                                                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </td>
                                                    <td className="py-3 px-3 text-right">
                                                        <div className="inline-flex items-center gap-1">
                                                            <button
                                                                onClick={() => startEdit(it)}
                                                                disabled={editingId !== null || !canWrite}
                                                                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1B365D] disabled:opacity-40 disabled:cursor-not-allowed"
                                                                data-testid={`litige-edit-${it.id}`}
                                                                title={!canWrite ? 'Accès refusé : permission Écriture requise' : 'Modifier'}
                                                            >
                                                                <Pencil className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => remove(it)}
                                                                disabled={editingId !== null || !canDelete}
                                                                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                                                data-testid={`litige-delete-${it.id}`}
                                                                title={!canDelete ? 'Accès refusé : permission Suppression requise' : 'Supprimer'}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}

/* ---------- Sub-components ---------- */

function FilesList({ files }) {
    if (!files || !files.length) return <span className="text-gray-300 text-xs">—</span>;
    return (
        <div className="flex flex-col gap-1">
            {files.map((f, i) => (
                <a
                    key={i}
                    href={`${API_URL}${f.url}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-blue-600 hover:underline text-xs truncate"
                    title={f.name}
                >
                    {isImage(f.name)
                        ? <ImageIcon className="w-3.5 h-3.5 shrink-0" />
                        : /\.pdf$/i.test(f.name) ? <FileText className="w-3.5 h-3.5 shrink-0" />
                            : <Paperclip className="w-3.5 h-3.5 shrink-0" />}
                    <span className="truncate max-w-[160px]">{f.name}</span>
                </a>
            ))}
        </div>
    );
}


function ClientAutocomplete({ value, onChange, allClients }) {
    const [open, setOpen] = useState(false);

    const matches = useMemo(() => {
        const q = (value || '').trim().toLowerCase();
        if (!q) return [];
        return allClients
            .filter((c) => (c.nom || '').toLowerCase().includes(q))
            .slice(0, 10);
    }, [value, allClients]);

    return (
        <Popover open={open && matches.length > 0} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <Input
                    value={value || ''}
                    placeholder="Rechercher un client…"
                    autoComplete="off"
                    onChange={(e) => { onChange({ client_id: null, client_nom: e.target.value }); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    className="h-9"
                    data-testid="litige-client-input"
                />
            </PopoverAnchor>
            <PopoverContent
                align="start"
                sideOffset={4}
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="p-0 w-[280px] max-h-64 overflow-y-auto bg-white border border-gray-200 shadow-xl"
            >
                {matches.map((c) => (
                    <button
                        key={c.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-0 block"
                        onClick={() => {
                            onChange({ client_id: c.id, client_nom: c.nom });
                            setOpen(false);
                        }}
                    >
                        <p className="font-medium text-[#1B365D]">{c.nom}</p>
                        {(c.telephone || c.email) && (
                            <p className="text-xs text-gray-500">{c.telephone || c.email}</p>
                        )}
                    </button>
                ))}
            </PopoverContent>
        </Popover>
    );
}


function EditableRow({ draft, setDraft, onSave, onCancel, onFiles, onRemoveKept, onRemoveNew, fileRef, saving, allClients }) {
    const totalFiles = (draft.files?.length || 0) + (draft.new_files?.length || 0);
    return (
        <tr className="border-b bg-blue-50/30 align-top">
            <td className="py-3 px-3">
                <ClientAutocomplete
                    value={draft.client_nom}
                    allClients={allClients}
                    onChange={({ client_id, client_nom }) => setDraft({ ...draft, client_id, client_nom })}
                />
            </td>
            <td className="py-3 px-3">
                <Input
                    type="date"
                    value={draft.date || ''}
                    onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                    className="h-9"
                    data-testid="litige-date-input"
                />
            </td>
            <td className="py-3 px-3">
                <Textarea
                    value={draft.description || ''}
                    placeholder="Décrire le problème…"
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    className="min-h-[72px]"
                    data-testid="litige-description-input"
                />
            </td>
            <td className="py-3 px-3">
                <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPT_FILES}
                    multiple
                    className="hidden"
                    onChange={(e) => onFiles(e.target.files)}
                />
                <Button
                    type="button" size="sm" variant="outline"
                    className="h-9 w-full"
                    onClick={() => fileRef.current?.click()}
                    data-testid="litige-pdf-btn"
                >
                    <Upload className="w-3 h-3 mr-1" />
                    {totalFiles > 0 ? `Ajouter (${totalFiles})` : 'Joindre'}
                </Button>
                {(draft.files?.length > 0 || draft.new_files?.length > 0) && (
                    <div className="mt-1 space-y-1 max-w-[180px]">
                        {(draft.files || []).map((f, i) => (
                            <div key={`k${i}`} className="flex items-center justify-between gap-1 text-[10px]">
                                <a href={`${API_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline truncate flex-1" title={f.name}>
                                    {f.name}
                                </a>
                                <button type="button" onClick={() => onRemoveKept(i)} className="text-red-500 hover:text-red-700">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                        {(draft.new_files || []).map((f, i) => (
                            <div key={`n${i}`} className="flex items-center justify-between gap-1 text-[10px]">
                                <span className="text-amber-700 truncate flex-1" title={`${f.file_name} (à enregistrer)`}>
                                    + {f.file_name}
                                </span>
                                <button type="button" onClick={() => onRemoveNew(i)} className="text-red-500 hover:text-red-700">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </td>
            <td className="py-3 px-3">
                <Input
                    type="number" step="0.01" min="0"
                    value={draft.cout ?? ''}
                    placeholder="Ex: 150"
                    onChange={(e) => setDraft({ ...draft, cout: e.target.value })}
                    className="h-9"
                    data-testid="litige-cout-input"
                />
            </td>
            <td className="py-3 px-3">
                <Select value={draft.statut || 'a_traiter'} onValueChange={(v) => setDraft({ ...draft, statut: v })}>
                    <SelectTrigger className="h-9" data-testid="litige-statut-input">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {Object.entries(STATUT_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </td>
            <td className="py-3 px-3 text-right">
                <div className="inline-flex flex-col gap-1">
                    <Button
                        type="button" size="sm"
                        onClick={onSave}
                        disabled={saving}
                        className="bg-[#059669] hover:bg-[#047857] text-white h-8"
                        data-testid="litige-save-btn"
                    >
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" />Enregistrer</>}
                    </Button>
                    <Button
                        type="button" size="sm" variant="ghost"
                        onClick={onCancel}
                        disabled={saving}
                        className="h-7 text-gray-500 hover:text-red-600"
                        data-testid="litige-cancel-btn"
                    >
                        <X className="w-3 h-3 mr-1" />Annuler
                    </Button>
                </div>
            </td>
        </tr>
    );
}
